import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { captureMessage } from '@sentry/react';
import { timerStatuses } from '../constants';
import { FIRESTORE_COLLECTIONS } from '../utils/firebase/constants';
import { useFirebase } from '../utils/firebase/FirebaseProvider';
import useThreadMeta from './useThreadMeta';
import useToast from './useToast';
import usePreferences from './usePreferences';
import { toggleTimepad } from '../redux/modules/views/actions';
import { noop } from '../utils';

export const pickProperDoc = data => {
  if (!data.length) {
    return null;
  }
  const runningTimer = data.find(el => el.status === timerStatuses.RUNNING);
  const stoppedTimer = data.find(el => el.status === timerStatuses.STOPPED);
  const initiatedTimer = data.find(el => el.status === timerStatuses.INITIATED);
  return runningTimer || stoppedTimer || initiatedTimer;
};

export default ({ startTimer, ticketId } = {}) => {
  const { threadMeta, updateThreadMeta } = useThreadMeta({ ticketId });
  const { memberId, company, fullname } = useSelector(store => store.users.user);
  const {
    getDocuments,
    createDocument,
    updateDocument,
    isLoggedIn: isLoggedInToFirebase
  } = useFirebase();
  const {
    preferences: { openTimepadOnStartTimer }
  } = usePreferences();
  const dispatch = useDispatch();
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const intervalRef = useRef(null);
  const enqueuedPayload = useRef();
  const baseTimerParams = {
    memberId,
    memberFullName: fullname,
    ticketId,
    companyId: company.id,
    isInternal: true,
    isResolution: false
  };
  const baseDBParams = { collectionName: FIRESTORE_COLLECTIONS.timers };
  const dbFilters = [
    {
      key: 'ticketId',
      operator: '==',
      value: ticketId || ''
    },
    {
      key: 'status',
      operator: 'in',
      value: [timerStatuses.INITIATED, timerStatuses.RUNNING, timerStatuses.STOPPED]
    }
  ];

  const stoppedTime = useMemo(() => {
    if (threadMeta.timerStartedAt && threadMeta.timerStoppedAt) {
      return Math.max(
        Math.round((threadMeta.timerStoppedAt - threadMeta.timerStartedAt) / 1000),
        0
      );
    }
    return 0;
  }, [threadMeta.timerStartedAt, threadMeta.timerStoppedAt]);

  const getOrCreateTimerDocument = async () => {
    const existingDoc = await getDocuments({
      collectionName: FIRESTORE_COLLECTIONS.timers,
      filters: dbFilters,
      dataNormalizer: pickProperDoc
    });
    if (existingDoc) {
      return existingDoc;
    }
    const createdDoc = await createDocument(
      { ...baseTimerParams, status: timerStatuses.INITIATED },
      { ...baseDBParams, shouldReturnDoc: true }
    );
    return createdDoc;
  };

  const updateTimerDoc = useCallback(
    async payload => {
      if (!isLoggedInToFirebase) return;

      let { timerDocId } = threadMeta;
      if (!timerDocId) {
        // If there is no `id`, the doc will be created/fetched in getOrCreateTimerDocument below.
        // To prevent multiple doc creation/fetch, here payload is enqueued and subsequent calls are prevented
        if (enqueuedPayload.current) {
          enqueuedPayload.current = { ...enqueuedPayload.current, ...payload };
          return;
        }
        enqueuedPayload.current = { ...enqueuedPayload.current, ...payload };
        const data = await getOrCreateTimerDocument();
        timerDocId = data.id;
      }
      const latestPayload = { ...payload, ...enqueuedPayload.current, memberFullName: fullname };
      enqueuedPayload.current = null;
      const updatedDoc = await updateDocument(latestPayload, { ...baseDBParams, id: timerDocId });
      return updatedDoc;
    },
    [threadMeta.timerDocId, ticketId]
  );

  const handleStart = ({ ignoreTimepadOpen = false } = {}) => {
    if (threadMeta.isTimerStopped) {
      // we don't support resuming if timer is stopped
      return;
    }
    setIsLoading(true);
    if (!ignoreTimepadOpen && openTimepadOnStartTimer) dispatch(toggleTimepad(true));
    if (!threadMeta.isTimerActive) {
      updateTimerDoc({ status: timerStatuses.RUNNING, startedAt: new Date() })
        .catch(err => {
          toast.error(`Error while starting timer! ${err.message}`);
        })
        .finally(() => setIsLoading(false));
    } else {
      captureMessage('Attempt to start already running timer', {
        tags: {
          ticketId
        }
      });
      toast.warn('Timer is already running!');
      setIsLoading(false);
    }
  };

  const handleReset = isSubmitted => {
    clearInterval(intervalRef.current);
    if (isSubmitted) {
      updateTimerDoc({
        status: timerStatuses.SUBMITTED,
        submittedAt: new Date(),
        stoppedAt: 0,
        startedAt: 0
      }).catch(() => toast.error('Error while stopping timer!'));
    } else {
      updateTimerDoc({ status: timerStatuses.INITIATED, stoppedAt: 0, startedAt: 0 }).catch(() =>
        toast.error('Error while clearing timer!')
      );
    }
    setTimer(0);
  };

  const handleStop = () => {
    clearInterval(intervalRef.current);
    updateTimerDoc({ status: timerStatuses.STOPPED, stoppedAt: new Date() }).catch(() =>
      toast.error('Error while stopping timer!')
    );
    setTimer(0);
  };

  const handleUpdateTimerData = useCallback(
    payload => {
      updateThreadMeta({ isDraftSaving: true });
      if (payload) {
        updateTimerDoc(payload)
          .catch(err => {
            toast.error(`Error while updating timer! ${err.message}`, {
              duration: 5000
            });
          })
          .finally(() => updateThreadMeta({ isDraftSaving: false }));
      }
    },
    [updateTimerDoc]
  );

  const getTimerValue = () => {
    if (threadMeta.timerStartedAt && threadMeta.isTimerActive) {
      return Math.round((Date.now() - threadMeta.timerStartedAt) / 1000);
    }
    return 0;
  };

  useEffect(() => {
    if (!threadMeta.isTimerActive || !threadMeta.timerStartedAt || !startTimer) {
      return noop;
    }

    intervalRef.current = setInterval(() => {
      setTimer(Math.round((Date.now() - threadMeta.timerStartedAt) / 1000));
    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [threadMeta.isTimerActive]);

  useEffect(() => {
    const { timerStartedAt, timerStoppedAt } = threadMeta;
    // if timer is actively running (not stopped yet)
    if (timerStartedAt && startTimer && threadMeta.isTimerActive) {
      setTimer(Math.round((Date.now() - timerStartedAt) / 1000));
    }
    // reset in case we've got a timer which was never started
    else if (timer > 0 && timerStartedAt === 0 && timerStoppedAt === 0) {
      setTimer(0);
    }
  }, [threadMeta.timerStartedAt, threadMeta.timerStoppedAt, threadMeta.isTimerActive]);

  useEffect(() => {
    if (!threadMeta.isTimerActive) setTimer(0);
  }, [threadMeta.isTimerActive]);

  return {
    timer,
    getTimerValue,
    isActiveTimer: Boolean(threadMeta.isTimerActive),
    isStoppedTimer: Boolean(threadMeta.isTimerStopped),
    stoppedTime,
    handleStart,
    handleReset,
    handleStop,
    handleUpdateTimerData,
    isLoading,
    isDraftSaving: threadMeta.isDraftSaving
  };
};
